/**
 * FormResponseViewer
 *
 * Renders a completed QuestionnaireResponse in the same visual style as
 * AssessmentResponsePage — label/value pairs, nested groups, rich-text consent
 * blocks rendered as HTML (not raw tags).
 *
 * Also exports helpers to print or download completed forms without opening a new tab.
 */

import { Box, Divider, Stack, Text } from '@mantine/core';
import { blobFromBytes, printHtmlDocument, sanitizeDownloadFilename, triggerBlobDownload } from './fileDownload';
import { buildFormResponsePdfBytes } from './formResponsePdf';
import type { QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import { SignatureDisplayBox } from '../components/SignatureDisplayBox';
import {
  CLIENT_SIGNATURE_EXT_URL,
  getSignatureFromQR,
} from '../pages/appointments/fhirNoteMapping';
import {
  getResponseItemDisplayMode,
  looksLikeHtml,
  plainAnswerText,
  plainQuestionText,
  richHtmlForResponseItem,
} from './formResponseDisplay';
import { buildClientSignatureBlockHtml } from './clientSignatureBlockHtml';
import './FormResponseViewer.css';

function signatureHtml(qr: QuestionnaireResponse): string {
  const signature = getSignatureFromQR(qr, CLIENT_SIGNATURE_EXT_URL);
  if (!signature) return '';
  return buildClientSignatureBlockHtml(signature);
}

// ---------------------------------------------------------------------------
// PDF printing
// ---------------------------------------------------------------------------

function itemsToHtml(items: QuestionnaireResponseItem[], depth = 0): string {
  return items
    .map((item) => {
      const hasChildren = (item.item ?? []).length > 0;
      const indent = depth * 16;

      if (hasChildren) {
        const groupLabel = item.text
          ? `<div class="group-label" style="margin-left:${indent}px">${plainQuestionText(item)}</div>`
          : '';
        return `${groupLabel}${itemsToHtml(item.item ?? [], depth + 1)}`;
      }

      if (getResponseItemDisplayMode(item) === 'rich-html') {
        return `
        <div class="rich-text" style="margin-left:${indent}px">
          ${richHtmlForResponseItem(item)}
        </div>`;
      }

      const q = plainQuestionText(item);
      const answerText = plainAnswerText(item);
      return `
        <div class="item" style="margin-left:${indent}px">
          <div class="question">${q}</div>
          <div class="answer">${answerText || '—'}</div>
        </div>`;
    })
    .join('');
}

function buildFormDocumentHtml(title: string, qr: QuestionnaireResponse): string {
  const dateStr = qr.authored
    ? new Date(qr.authored).toLocaleDateString('en-US', { dateStyle: 'long' })
    : '';

  const bodyHtml = itemsToHtml(qr.item ?? []);
  const signatureBlock = signatureHtml(qr);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; margin: 40px; color: #1a1a2e; font-size: 13px; }
    h1 { font-size: 18px; font-weight: 700; margin: 0 0 4px 0; }
    .meta { color: #666; font-size: 12px; margin-bottom: 24px; border-bottom: 1px solid #e5e7eb; padding-bottom: 12px; }
    .item { margin-bottom: 14px; }
    .question { font-weight: 600; font-size: 13px; margin-bottom: 3px; color: #111; }
    .answer { font-size: 13px; color: #444; white-space: pre-wrap; }
    .group-label { font-size: 14px; font-weight: 700; margin: 20px 0 8px 0;
                   border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; color: #333; }
    .rich-text ol, .rich-text ul { padding-left: 1.5em; }
    .rich-text li { margin-bottom: 0.35em; }
    .rich-text p { margin: 0 0 0.5em 0; }
    @media print {
      body { margin: 20px; }
      @page { margin: 20mm 15mm; }
    }
  </style>
</head>
<body>
  <h1>${title}</h1>
  <div class="meta">${dateStr ? `Completed: ${dateStr}` : 'Completed form'}</div>
  ${bodyHtml}
  ${signatureBlock}
</body>
</html>`;
}

/** Opens the browser print dialog on the current page (no new tab). */
export function printFormDocument(title: string, qr: QuestionnaireResponse): void {
  printHtmlDocument(buildFormDocumentHtml(title, qr));
}

/** Downloads the completed form as a PDF file without opening a new tab. */
export async function downloadFormAsPdf(title: string, qr: QuestionnaireResponse): Promise<void> {
  const pdfBytes = await buildFormResponsePdfBytes(title, qr);
  triggerBlobDownload(
    blobFromBytes(pdfBytes, 'application/pdf'),
    sanitizeDownloadFilename(title, '.pdf'),
  );
}

/** @deprecated Use downloadFormAsPdf or printFormDocument */
export async function printFormAsPdf(title: string, qr: QuestionnaireResponse): Promise<void> {
  await downloadFormAsPdf(title, qr);
}

// ---------------------------------------------------------------------------
// React renderer
// ---------------------------------------------------------------------------

function RichTextBlock({ html }: { html: string }): JSX.Element {
  return (
    <Box
      className="rich-text-preview"
      dangerouslySetInnerHTML={{ __html: html }}
      style={{ fontSize: 'var(--mantine-font-size-sm)', lineHeight: 1.6 }}
    />
  );
}

function ResponseItemRow({ item, depth = 0 }: { item: QuestionnaireResponseItem; depth?: number }): JSX.Element {
  const hasChildren = (item.item ?? []).length > 0;
  const pl = depth * 16;

  if (hasChildren) {
    const groupLabel = plainQuestionText(item);
    return (
      <Box pl={pl}>
        {groupLabel && (
          <Text size="sm" fw={700} mb={6} mt={depth > 0 ? 12 : 0} c="dark.7">
            {groupLabel}
          </Text>
        )}
        <Stack gap="sm">
          {(item.item ?? []).map((child) => (
            <ResponseItemRow key={child.linkId ?? child.text ?? ''} item={child} depth={depth + 1} />
          ))}
        </Stack>
      </Box>
    );
  }

  if (getResponseItemDisplayMode(item) === 'rich-html') {
    return (
      <Box pl={pl}>
        <RichTextBlock html={richHtmlForResponseItem(item)} />
      </Box>
    );
  }

  const questionText = plainQuestionText(item);
  const answerText = plainAnswerText(item);

  return (
    <Box pl={pl}>
      {questionText && (
        <Text size="sm" fw={600} c="dark.8" mb={3}>
          {questionText}
        </Text>
      )}
      <Text size="sm" c="gray.7" style={{ whiteSpace: 'pre-wrap' }}>
        {answerText || '—'}
      </Text>
    </Box>
  );
}

interface FormResponseViewerProps {
  questionnaireResponse: QuestionnaireResponse;
}

/**
 * Renders all items in a QuestionnaireResponse using label/value pairs for
 * standard fields and sanitized HTML for rich-text consent blocks.
 */
export function FormResponseViewer({ questionnaireResponse }: FormResponseViewerProps): JSX.Element {
  const items = questionnaireResponse.item ?? [];
  const clientSignature = getSignatureFromQR(questionnaireResponse, CLIENT_SIGNATURE_EXT_URL);

  if (items.length === 0 && !clientSignature) {
    return (
      <Text size="sm" c="gray.6" ta="center" py="md">
        No responses recorded.
      </Text>
    );
  }

  return (
    <Stack gap="md">
      {items.map((item, i) => (
        <Box key={item.linkId ?? item.text ?? i}>
          <ResponseItemRow item={item} />
          {i < items.length - 1 && <Divider mt="md" />}
        </Box>
      ))}
      {clientSignature && (
        <>
          {items.length > 0 && <Divider />}
          <SignatureDisplayBox signature={clientSignature} label="Client signature" />
        </>
      )}
    </Stack>
  );
}

// Re-export for tests / callers that inspect HTML answers
export { looksLikeHtml };
